import { groupBy, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ProductionComponentInstance } from '@paragon/contract-library';
import { naturalSortByProperty } from '@paragon/utilities';

import { HeightCache, WidthCache } from '../ComponentGeometryCaches';
import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getBundlesByScheduleGroupGuid } from './getBundlesByScheduleGroupGuid';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab } from './getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab';
import { getLoadedTrussesByAnalysisSetGuid, getTrussMeasurements } from './trusses';

export const getProductionComponentInstancesByScheduleGroupGuid = createSelector(
  (state: RootState) => state.scheduleGroups,
  getBundlesByScheduleGroupGuid,
  getProductionComponentInstancesByBundleGuid,

  (scheduleGroups, bundlesByScheduleGroupGuid, productionComponentInstancesByBundleGuid) =>
    mapValues(scheduleGroups, (scheduleGroup) =>
      bundlesByScheduleGroupGuid[scheduleGroup.guid].flatMap(
        (bundle) => productionComponentInstancesByBundleGuid[bundle.guid],
      ),
    ),
);

type GridGroupedProductionComponentInstance = {
  /** Used as a unique identifier; set to GUID of first production component instance */
  readonly guid: string;
  readonly analysisSetGuid: string;
  readonly trussName: string;
  readonly instances: readonly ProductionComponentInstance[];
} & AggregatedTrussStatistics;
export const getGroupedProductionComponentInstancesByBundleGuid = createSelector(
  getProductionComponentInstancesByBundleGuid,
  getLoadedTrussesByAnalysisSetGuid,
  getTrussMeasurements,

  (productionComponentInstancesByBundleGuid, loadedTrussesByAnalysisSetGuid, trussMeasurements) =>
    mapValues(productionComponentInstancesByBundleGuid, (productionComponentInstancesForBundle) => {
      const productionComponentInstancesForBundleGroupedByAnalysisSetGuid = groupBy(
        productionComponentInstancesForBundle,
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
      );
      const groupedProductionComponentInstances: readonly GridGroupedProductionComponentInstance[] = Object.entries(
        productionComponentInstancesForBundleGroupedByAnalysisSetGuid,
      ).map(([analysisSetGuid, group]) => ({
        guid: group[0].guid,
        analysisSetGuid,
        trussName: loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name ?? '',
        trussCount: group.length,
        boardFeet: (trussMeasurements[analysisSetGuid]?.boardFeet ?? 0) * group.length,
        linearFeet: (trussMeasurements[analysisSetGuid]?.linearFeet ?? 0) * group.length,
        heights: loadedTrussesByAnalysisSetGuid[analysisSetGuid]
          ? [HeightCache.get(analysisSetGuid, loadedTrussesByAnalysisSetGuid[analysisSetGuid])]
          : [],
        widths: loadedTrussesByAnalysisSetGuid[analysisSetGuid]
          ? [WidthCache.get(analysisSetGuid, loadedTrussesByAnalysisSetGuid[analysisSetGuid])]
          : [],
        thicknesses: loadedTrussesByAnalysisSetGuid[analysisSetGuid]
          ? [loadedTrussesByAnalysisSetGuid[analysisSetGuid].thickness]
          : [],
        instances: group,
      }));
      return naturalSortByProperty(
        groupedProductionComponentInstances,
        (groupedProductionComponentInstance) => groupedProductionComponentInstance.trussName,
      );
    }),
);

export const getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabByAnalysisSetGuid =
  createSelector(
    getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab,

    (productionComponentInstancesForActiveProductionGroup) =>
      groupBy(
        productionComponentInstancesForActiveProductionGroup,
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
      ),
  );
